import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * useServiceWorkerUpdater (iter100p)
 * -----------------------
 * Registra `/sw.js` UNA SOLA VOLTA e mostra il toast "Nuova versione
 * disponibile" SOLO quando la versione ricevuta dal SW è DIVERSA dalla
 * versione già nota (persistita in localStorage).
 *
 * Fix iter100p rispetto alla versione precedente:
 * - Rimosso il refresh update() sul window `focus` (troppo aggressivo:
 *   causava toast fantasma quando cambiavano solo header CDN)
 * - Confronto versione con localStorage per non mostrare toast al primo
 *   avvio o quando la versione è la stessa già installata
 * - Un solo update() dopo 10 secondi dall'avvio (best-effort, silenzioso)
 */
const SW_VERSION_STORAGE_KEY = 'cr_sw_version';

export default function useServiceWorkerUpdater() {
  const registeredRef = useRef(false);
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    if (registeredRef.current) return;
    registeredRef.current = true;

    const showUpdateToast = (version) => {
      if (notifiedRef.current) return;
      notifiedRef.current = true;
      toast('Nuova versione disponibile', {
        description: 'È stata installata una nuova versione dell\'app. Ricarica per applicare gli aggiornamenti.',
        duration: Infinity,
        action: {
          label: 'Ricarica',
          onClick: () => {
            try { window.localStorage.setItem(SW_VERSION_STORAGE_KEY, version); } catch { /* noop */ }
            try { window.location.reload(); } catch { /* noop */ }
          },
        },
        cancel: {
          label: 'Più tardi',
          onClick: () => {
            // Salva la versione così non mostro di nuovo il toast nella stessa sessione
            try { window.localStorage.setItem(SW_VERSION_STORAGE_KEY, version); } catch { /* noop */ }
          }
        },
      });
    };

    const registerSw = async () => {
      try {
        let registration = await navigator.serviceWorker.getRegistration('/sw.js');
        if (!registration) {
          registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        }
        // Un solo update check dopo 10 secondi (permette al bundle di caricare
        // prima). NIENTE update su focus: causava toast fantasma.
        setTimeout(() => {
          try { registration.update(); } catch { /* noop */ }
        }, 10000);
      } catch (e) {
        // In dev environment il SW può fallire per HTTPS, non è critico
      }
    };

    const onMessage = (event) => {
      const data = event && event.data;
      if (!data) return;
      if (data.type === 'SW_UPDATED' && data.version) {
        // Confronta con la versione già installata (persistita)
        let previousVersion = null;
        try { previousVersion = window.localStorage.getItem(SW_VERSION_STORAGE_KEY); } catch { /* noop */ }
        // Se è la prima installazione (nessuna versione salvata) → salva silenziosamente
        if (!previousVersion) {
          try { window.localStorage.setItem(SW_VERSION_STORAGE_KEY, data.version); } catch { /* noop */ }
          return;
        }
        // Se la versione è la stessa già installata → non fare nulla
        if (previousVersion === data.version) {
          return;
        }
        // Solo se c'è una versione DIVERSA mostra il toast
        showUpdateToast(data.version);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);

    registerSw();

    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, []);
}
